import React, { useState, useEffect } from "react";
import {
  fetchAppointmentLogs,
  fetchUsers,
  createAppointmentLog,
  updateAppointmentLog,
  deleteAppointmentLog,
} from "./api";
import { toast } from "react-toastify";
import Select from "react-select";
import "react-toastify/dist/ReactToastify.css";

function AppointmentLogs() {
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newLog, setNewLog] = useState({
    user_id: "",
    event_from_date: "",
    event_to_date: "",
    start_time: "", // Nouveau champ
    end_time: "", // Nouveau champ
    reason: "",
  });
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [logToEdit, setLogToEdit] = useState(null);

  const fetchAllLogs = async () => {
    try {
      const response = await fetchAppointmentLogs();
      setLogs(response.data);
    } catch (error) {
      toast.error("Failed to fetch appointment logs");
    }
  };

  const fetchAllUsers = async () => {
    try {
      const response = await fetchUsers();
      setUsers(response.data);
    } catch (error) {
      toast.error("Failed to fetch users");
    }
  };

  useEffect(() => {
    fetchAllLogs();
    fetchAllUsers();
  }, []);

  const handleInputChange = (field, value) => {
    setNewLog({ ...newLog, [field]: value });
  };

  const handleEditInputChange = (field, value) => {
    setLogToEdit({ ...logToEdit, [field]: value });
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const formatTime = (timeString) => {
    if (!timeString) return "";
    // On suppose que le format est HH:mm:ss, on retourne HH:mm
    return timeString.slice(0, 5);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const d = new Date(dateString);
    return `${String(d.getDate()).padStart(2, "0")}/${String(
      d.getMonth() + 1
    ).padStart(2, "0")}/${d.getFullYear()}`;
  };

  const handleCreateLog = async () => {
    try {
      await createAppointmentLog(newLog);
      fetchAllLogs();
      toast.success("Appointment log created successfully");
      setIsModalOpen(false);
      // Réinitialiser le formulaire
      setNewLog({
        user_id: "",
        event_from_date: "",
        event_to_date: "",
        start_time: "",
        end_time: "",
        reason: "",
      });
    } catch (error) {
      toast.error("Failed to create appointment log");
    }
  };

  const handleEditLog = async () => {
    try {
      await updateAppointmentLog(logToEdit.id, logToEdit);
      fetchAllLogs();
      toast.success("Appointment log updated successfully");
      setIsEditModalOpen(false);
    } catch (error) {
      toast.error("Failed to update appointment log");
    }
  };

  const handleDeleteLog = async (id) => {
    try {
      await deleteAppointmentLog(id);
      fetchAllLogs();
      toast.success("Appointment log deleted successfully");
    } catch (error) {
      toast.error("Failed to delete appointment log");
    }
  };

  const userOptions = users.map((user) => ({
    value: user.id,
    label: `${user.firstName} ${user.lastName}`,
  }));

  return (
    <div className="container mx-auto p-6">
      <h1 className="text-3xl font-semibold mb-6 text-center">
        Manage Appointment Logs
      </h1>
      <button
        onClick={() => setIsModalOpen(true)}
        className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded mb-4"
      >
        Create Appointment Log
      </button>
      <table className="min-w-full bg-white border border-gray-300 rounded-lg shadow-md table-auto">
        <thead className="bg-gray-800 text-white">
          <tr>
            <th className="px-4 py-2">User</th>
            <th className="px-4 py-2">Event From</th>
            <th className="px-4 py-2">Start Time</th>
            <th className="px-4 py-2">Event To</th>
            <th className="px-4 py-2">End Time</th>
            <th className="px-4 py-2">Reason</th>
            <th className="px-4 py-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          {logs.map((log) => (
            <tr key={log.id} className="border-t">
              <td className="px-4 py-2">
                {log.firstName} {log.lastName}
              </td>
              <td className="px-4 py-2">{formatDate(log.event_from_date)}</td>
              <td className="px-4 py-2">{formatTime(log.start_time)}</td>
              <td className="px-4 py-2">{formatDate(log.event_to_date)}</td>
              <td className="px-4 py-2">{formatTime(log.end_time)}</td>
              <td className="px-4 py-2">{log.reason}</td>
              <td className="px-4 py-2 flex space-x-2">
                <button
                  onClick={() => {
                    setLogToEdit(log);
                    setIsEditModalOpen(true);
                  }}
                  className="bg-green-500 hover:bg-green-700 text-white font-bold py-1 px-2 rounded"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDeleteLog(log.id)}
                  className="bg-red-500 hover:bg-red-700 text-white font-bold py-1 px-2 rounded"
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Modal for creating a new appointment log */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <h2 className="text-2xl font-bold mb-4">Create Appointment Log</h2>
            <div className="mb-4">
              <label className="block text-gray-700">User</label>
              <Select
                options={userOptions}
                value={userOptions.find(
                  (option) => option.value === newLog.user_id
                )}
                onChange={(option) =>
                  handleInputChange("user_id", option.value)
                }
                className="w-full"
                isSearchable
                placeholder="Select user"
              />
            </div>
            <div className="mb-4">
              <label className="block text-gray-700">Event From</label>
              <input
                type="date"
                value={newLog.event_from_date}
                onChange={(e) =>
                  handleInputChange("event_from_date", e.target.value)
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm"
              />
            </div>
            <div className="mb-4">
              <label className="block text-gray-700">Start Time</label>
              <input
                type="time"
                value={newLog.start_time}
                onChange={(e) =>
                  handleInputChange("start_time", e.target.value)
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm"
              />
            </div>
            <div className="mb-4">
              <label className="block text-gray-700">Event To</label>
              <input
                type="date"
                value={newLog.event_to_date}
                onChange={(e) =>
                  handleInputChange("event_to_date", e.target.value)
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm"
              />
            </div>
            <div className="mb-4">
              <label className="block text-gray-700">End Time</label>
              <input
                type="time"
                value={newLog.end_time}
                onChange={(e) => handleInputChange("end_time", e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm"
              />
            </div>
            <div className="mb-4">
              <label className="block text-gray-700">Reason</label>
              <input
                type="text"
                value={newLog.reason}
                onChange={(e) => handleInputChange("reason", e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm"
              />
            </div>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setIsModalOpen(false)}
                className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateLog}
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal for editing an appointment log */}
      {isEditModalOpen && logToEdit && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <h2 className="text-2xl font-bold mb-4">Edit Appointment Log</h2>
            <div className="mb-4">
              <label className="block text-gray-700">User</label>
              <p className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm bg-gray-100">
                {logToEdit.firstName} {logToEdit.lastName}
              </p>
            </div>
            <div className="mb-4">
              <label className="block text-gray-700">Event From</label>
              <input
                type="date"
                value={formatDateForInput(logToEdit.event_from_date)}
                onChange={(e) =>
                  setLogToEdit({
                    ...logToEdit,
                    event_from_date: e.target.value,
                  })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm"
              />
            </div>
            <div className="mb-4">
              <label className="block text-gray-700">Start Time</label>
              <input
                type="time"
                value={logToEdit.start_time || ""}
                onChange={(e) =>
                  setLogToEdit({ ...logToEdit, start_time: e.target.value })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm"
              />
            </div>
            <div className="mb-4">
              <label className="block text-gray-700">Event To</label>
              <input
                type="date"
                value={formatDateForInput(logToEdit.event_to_date)}
                onChange={(e) =>
                  setLogToEdit({ ...logToEdit, event_to_date: e.target.value })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm"
              />
            </div>
            <div className="mb-4">
              <label className="block text-gray-700">End Time</label>
              <input
                type="time"
                value={logToEdit.end_time || ""}
                onChange={(e) =>
                  setLogToEdit({ ...logToEdit, end_time: e.target.value })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm"
              />
            </div>
            <div className="mb-4">
              <label className="block text-gray-700">Reason</label>
              <input
                type="text"
                value={logToEdit.reason}
                onChange={(e) =>
                  setLogToEdit({ ...logToEdit, reason: e.target.value })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm"
              />
            </div>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setIsEditModalOpen(false)}
                className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleEditLog}
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AppointmentLogs;
